import { Box, Text, useTheme } from 'app/shopify/theme';
import { IconArrowLeft } from '@tabler/icons-react-native';
import React from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

type ButtonVariant = 'primary' | 'secondaryTonal' | 'secondaryNeutral' | 'tertiary' | 'ghost' | 'destructive';
type ButtonSize = 'small' | 'medium' | 'large';
type ButtonState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled' | 'loading';

export interface ButtonProps {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Si défini (autre que `enabled`), état figé pour doc / tests. Sinon interaction réelle : pressed + hover (web). */
  state?: ButtonState;
  disabled?: boolean;
  inverted?: boolean;
  fullWidth?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

const HORIZONTAL_PADDING_BY_SIZE: Record<
  ButtonSize,
  'spacing.general.x2' | 'spacing.general.x3' | 'spacing.general.x6'
> = {
  small: 'spacing.general.x2',
  medium: 'spacing.general.x3',
  large: 'spacing.general.x6',
};

// RN: avec une `height` fixe, un `paddingVertical` trop grand peut réduire la hauteur disponible
// et "cropper" le texte. On garde donc l'espacement vertical dans `label-wrapper`.
const VERTICAL_PADDING_BY_SIZE: Record<ButtonSize, 'spacing.general.0'> = {
  small: 'spacing.general.0',
  medium: 'spacing.general.0',
  large: 'spacing.general.0',
};

const ICON_SIZE_BY_SIZE: Record<ButtonSize, number> = {
  small: 16,
  medium: 24,
  large: 24,
};

const TEXT_VARIANT_BY_SIZE: Record<ButtonSize, 'bodySmallMedium' | 'bodyBaseMedium'> = {
  small: 'bodySmallMedium',
  medium: 'bodyBaseMedium',
  large: 'bodyBaseMedium',
};

const TEXT_COLOR_BY_VARIANT: Record<ButtonVariant, 'text.primary' | 'text.brand'> = {
  primary: 'text.primary',
  secondaryTonal: 'text.primary',
  secondaryNeutral: 'text.primary',
  tertiary: 'text.brand',
  ghost: 'text.brand',
  destructive: 'text.primary',
};

function getButtonHeight(size: ButtonSize, value: string | undefined) {
  if (!value) return size === 'large' ? 56 : size === 'medium' ? 40 : 32;
  const parsed = Number(value);
  if (Number.isFinite(parsed)) return parsed;
  return size === 'large' ? 56 : size === 'medium' ? 40 : 32;
}

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

type ButtonTheme = ReturnType<typeof useTheme>;

function getButtonToken(variant: ButtonVariant, inverted: boolean) {
  if (variant === 'primary') return inverted ? 'component.button.inverted' : 'component.button.primary';
  if (variant === 'secondaryTonal') return 'component.button.secondary';
  if (variant === 'secondaryNeutral') return 'component.button.tertiary';
  return null;
}

function getButtonTextColor(theme: ButtonTheme, variant: ButtonVariant, inverted: boolean) {
  if ((variant === 'primary' && !inverted) || variant === 'destructive') return theme.colors.white;
  if (inverted && (variant === 'tertiary' || variant === 'ghost')) return theme.colors['text.inverse.primary'];
  return theme.colors[TEXT_COLOR_BY_VARIANT[variant]];
}

function getButtonOverlayColor({
  inverted,
  showHover,
  showPressed,
  theme,
  variant,
}: {
  inverted: boolean;
  showHover: boolean;
  showPressed: boolean;
  theme: ButtonTheme;
  variant: ButtonVariant;
}) {
  if (!showPressed && !showHover) return undefined;
  if (variant === 'primary' && !inverted) {
    return theme.colors[showPressed ? 'state-layer.press-brand' : 'state-layer.hover-brand'];
  }
  if (variant === 'destructive') {
    return theme.colors[showPressed ? 'state-layer.inverse.press' : 'state-layer.inverse.hover'];
  }
  if (inverted) return theme.colors[showPressed ? 'state-layer.inverse.press' : 'state-layer.inverse.hover'];
  return theme.colors[showPressed ? 'state-layer.press' : 'state-layer.hover'];
}

function getButtonPresentation({
  disabled,
  inverted,
  size,
  state,
  theme,
  variant,
}: Required<Pick<ButtonProps, 'disabled' | 'inverted' | 'size' | 'variant'>> &
  Pick<ButtonProps, 'state'> & { theme: ButtonTheme }) {
  const isInteractive = state === undefined || state === 'enabled';
  const buttonToken = getButtonToken(variant, inverted);
  return {
    backgroundColor:
      variant === 'destructive'
        ? theme.colors['surface.error.default']
        : buttonToken
          ? theme.colors[buttonToken as keyof typeof theme.colors]
          : 'transparent',
    borderColor:
      variant === 'tertiary' ? theme.colors[inverted ? 'border.inverse.primary' : 'border.primary'] : 'transparent',
    buttonHeight: getButtonHeight(size, theme.tokens.sizing[`sizing.component.button.height.${size}`]),
    isDisabled: isInteractive ? disabled : state === 'disabled',
    isFocus: state === 'focus',
    isInteractive,
    isLoading: state === 'loading',
    textColor: getButtonTextColor(theme, variant, inverted),
  };
}

function ShopifyButtonIcon({
  icon,
  side,
  size,
}: {
  icon?: React.ReactNode;
  side: 'leading' | 'trailing';
  size: number;
}) {
  if (!icon) return null;
  return (
    <Box
      alignItems="center"
      height={size}
      justifyContent="center"
      marginLeft={side === 'trailing' ? 'spacing.general.x1' : undefined}
      marginRight={side === 'leading' ? 'spacing.general.x1' : undefined}
      width={size}
    >
      {icon}
    </Box>
  );
}

function ShopifyButtonInner({
  fullWidth,
  interaction,
  inverted,
  label,
  leadingIcon,
  presentation,
  size,
  state,
  theme,
  trailingIcon,
  variant,
}: {
  fullWidth: boolean;
  interaction?: PressableInteractionState;
  inverted: boolean;
  label: string;
  leadingIcon?: React.ReactNode;
  presentation: ReturnType<typeof getButtonPresentation>;
  size: ButtonSize;
  state?: ButtonState;
  theme: ButtonTheme;
  trailingIcon?: React.ReactNode;
  variant: ButtonVariant;
}) {
  const pressed = interaction?.pressed ?? false;
  const hovered = interaction?.hovered ?? false;
  const showPressed = presentation.isInteractive ? pressed : state === 'pressed';
  const showHover = presentation.isInteractive ? hovered && !pressed : state === 'hover';
  const overlayColor = getButtonOverlayColor({ inverted, showHover, showPressed, theme, variant });
  const iconSlotSize = ICON_SIZE_BY_SIZE[size];

  return (
    <Box
      alignItems="center"
      borderRadius="radius.full"
      borderWidth={variant === 'tertiary' ? 1 : 0}
      flexDirection="row"
      height={presentation.buttonHeight}
      justifyContent="center"
      paddingHorizontal={HORIZONTAL_PADDING_BY_SIZE[size]}
      paddingVertical={VERTICAL_PADDING_BY_SIZE[size]}
      style={{
        backgroundColor: presentation.backgroundColor,
        borderColor: presentation.borderColor,
        opacity: presentation.isDisabled ? 0.4 : 1,
      }}
      width={fullWidth ? '100%' : undefined}
    >
      {overlayColor ? (
        <Box
          borderRadius="radius.full"
          bottom={0}
          left={0}
          position="absolute"
          right={0}
          style={{ backgroundColor: overlayColor, zIndex: 0 }}
          top={0}
        />
      ) : null}
      <Box alignItems="center" flexDirection="row" justifyContent="center" style={{ zIndex: 1 }}>
        <ShopifyButtonIcon icon={leadingIcon} side="leading" size={iconSlotSize} />
        <Box
          alignItems="center"
          flexDirection="row"
          justifyContent="center"
          paddingBottom={size === 'small' ? 'spacing.general.0' : 'spacing.general.x0,5'}
          paddingHorizontal={size === 'small' ? 'spacing.general.x1' : 'spacing.general.x2'}
          paddingTop="spacing.general.0"
        >
          <Text style={{ color: presentation.textColor }} variant={TEXT_VARIANT_BY_SIZE[size]}>
            {label}
          </Text>
        </Box>
        <ShopifyButtonIcon icon={trailingIcon} side="trailing" size={iconSlotSize} />
        {presentation.isLoading ? (
          <Box marginLeft="spacing.general.x2">
            <ActivityIndicator color={presentation.textColor} size="small" />
          </Box>
        ) : null}
      </Box>
      {presentation.isFocus ? (
        <Box
          borderRadius="radius.full"
          bottom={-4}
          left={-4}
          position="absolute"
          right={-4}
          style={{ borderWidth: 2, borderColor: theme.colors['border.focused'] }}
          top={-4}
        />
      ) : null}
    </Box>
  );
}

export function Button({
  label,
  onPress,
  variant = 'primary',
  size = 'medium',
  state,
  disabled = false,
  inverted = false,
  fullWidth = false,
  leadingIcon,
  trailingIcon,
}: ButtonProps) {
  const theme = useTheme();
  const presentation = getButtonPresentation({ disabled, inverted, size, state, theme, variant });

  const pressableStyle: { alignSelf: 'stretch' | 'flex-start' } = { alignSelf: fullWidth ? 'stretch' : 'flex-start' };

  if (presentation.isInteractive) {
    return (
      <Pressable disabled={presentation.isDisabled || presentation.isLoading} onPress={onPress} style={pressableStyle}>
        {(pressableState) => (
          <ShopifyButtonInner
            fullWidth={fullWidth}
            interaction={{
              pressed: pressableState.pressed,
              hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
            }}
            inverted={inverted}
            label={label}
            leadingIcon={leadingIcon}
            presentation={presentation}
            size={size}
            state={state}
            theme={theme}
            trailingIcon={trailingIcon}
            variant={variant}
          />
        )}
      </Pressable>
    );
  }

  return (
    <Pressable disabled={presentation.isDisabled || presentation.isLoading} onPress={onPress} style={pressableStyle}>
      <IconArrowLeft strokeWidth={ICON_STROKE_WIDTH} color="red" size={48} />
      {/* {renderInner()} */}
    </Pressable>
  );
}
